import { ChangeDetectionStrategy, Component, type OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { MessageService } from 'primeng/api';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { BreadcrumbsComponent, type BreadcrumbItem } from '@/shared/breadcrumbs/breadcrumbs.component';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';

/**
 * Namespaces list — entry point of the namespace-centric admin area. Shows the
 * namespaces the user can administer (all of them for superadmins) and opens the
 * detail page with Members / API Keys / Domains / Settings tabs.
 */
@Component({
    selector: 'app-namespaces',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, DialogModule, InputTextModule, TableModule, BreadcrumbsComponent, EmptyStateComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrl: './namespaces.scss',
    template: `
        <div class="management-page">
            <app-breadcrumbs [model]="breadcrumbs" />
            <header class="overview-header">
                <div>
                    <p class="overview-eyebrow">Administration</p>
                    <h1>Tenants</h1>
                    <p class="overview-description">Manage members, API keys, sign-in domains and settings per tenant.</p>
                </div>
                @if (isSuperAdmin()) {
                    <div class="overview-actions">
                        <p-button label="Create Tenant" icon="pi pi-plus" (onClick)="openCreate()" />
                    </div>
                }
            </header>

            <section class="management-panel" aria-label="Tenants">
                <p-table class="management-table" [value]="visibleNamespaces()" [loading]="loading()" [scrollable]="true" selectionMode="single" (onRowSelect)="open($event.data)">
                    <ng-template #header>
                        <tr>
                            <th>Name</th>
                            <th>Key</th>
                            <th class="hidden md:table-cell">Id</th>
                            <th><span class="sr-only">Actions</span></th>
                        </tr>
                    </ng-template>
                    <ng-template #body let-ns>
                        <tr [pSelectableRow]="ns" class="cursor-pointer">
                            <td class="font-medium">
                                <div class="min-w-40">{{ ns.name ?? ns.Name }}</div>
                            </td>
                            <td>
                                <div class="min-w-32">{{ ns.key ?? ns.Key }}</div>
                            </td>
                            <td class="hidden md:table-cell text-sm text-color-secondary">{{ ns.id ?? ns.Id }}</td>
                            <td>
                                <div class="management-row-actions">
                                    <p-button label="Open" icon="pi pi-arrow-right" iconPos="right" [text]="true" [ariaLabel]="'Open ' + (ns.name ?? ns.Name)" (onClick)="open(ns)" />
                                </div>
                            </td>
                        </tr>
                    </ng-template>
                    <ng-template #emptymessage>
                        <tr>
                            <td colspan="4">
                                @if (isSuperAdmin()) {
                                    <app-empty-state icon="fa fa-building" title="No tenants yet" description="Create one to get started." />
                                } @else {
                                    <app-empty-state icon="fa fa-building" title="You don't administer any tenants" />
                                }
                            </td>
                        </tr>
                    </ng-template>
                </p-table>
            </section>
        </div>

        <p-dialog header="Create Tenant" [visible]="showCreate()" [modal]="true" [closable]="true" (visibleChange)="showCreate.set($event)" [style]="{ width: '28rem' }">
            <label class="block mb-1 font-medium" for="ns-key">Key</label>
            <p class="text-sm text-color-secondary mb-2">Unique, permanent identifier (cannot be changed later).</p>
            <input pInputText id="ns-key" [(ngModel)]="newKey" placeholder="unique-key" class="w-full mb-4" [disabled]="creating()" />
            <label class="block mb-1 font-medium" for="ns-name">Name</label>
            <input pInputText id="ns-name" [(ngModel)]="newName" placeholder="Human readable name" class="w-full mb-4" [disabled]="creating()" (keyup.enter)="create()" />
            <div class="flex justify-end gap-2">
                <button pButton type="button" class="p-button-text" (click)="showCreate.set(false)" [disabled]="creating()"><span class="p-button-label">Cancel</span></button>
                <button pButton type="button" (click)="create()" [loading]="creating()" [disabled]="creating() || !newKey().trim() || !newName().trim()">@if (creating()) {<i class="pi pi-spinner pi-spin p-button-loading-icon" aria-hidden="true"></i>}<span class="p-button-label">Create</span></button>
            </div>
        </p-dialog>
    `
})
export class NamespacesPage implements OnInit {
    private api = inject(CloudApiService);
    private router = inject(Router);
    private messageService = inject(MessageService);

    readonly breadcrumbs: BreadcrumbItem[] = [
        { label: 'Dashboard', routerLink: '/dashboard' },
        { label: 'Tenants', routerLink: '/admin/tenants' }
    ];

    loading = signal(false);
    showCreate = signal(false);
    creating = signal(false);
    newKey = signal('');
    newName = signal('');

    isSuperAdmin = computed(() => !!this.api.currentUser()?.superAdmin);

    // Superadmins administer everything; others see only namespaces where their role
    // grants member or namespace management.
    visibleNamespaces = computed(() => {
        const all = this.api.namespaces() ?? [];
        if (this.isSuperAdmin()) return all;
        return all.filter((ns) => {
            const id = ns.id ?? ns.Id;
            return this.api.hasCapability(id, 'members.manage') || this.api.hasCapability(id, 'namespace.manage');
        });
    });

    ngOnInit(): void {
        this.loading.set(true);
        Promise.all([this.api.getNamespaces(), this.api.getRoles()])
            .catch((err) => {
                this.messageService.add({ severity: 'error', summary: 'Failed to Load Tenants', detail: err?.error?.message || err?.message || 'An error occurred' });
            })
            .finally(() => this.loading.set(false));
    }

    open(ns: any) {
        this.router.navigate(['/admin/tenants', ns.id ?? ns.Id]);
    }

    openCreate() {
        this.newKey.set('');
        this.newName.set('');
        this.showCreate.set(true);
    }

    create() {
        if (!this.newKey().trim() || !this.newName().trim()) return;
        this.creating.set(true);
        this.api
            .createNamespace(this.newKey().trim(), this.newName().trim())
            .then((ns) => {
                this.showCreate.set(false);
                this.messageService.add({ severity: 'success', summary: 'Tenant Created', detail: ns.name });
            })
            .catch((err) => {
                this.messageService.add({ severity: 'error', summary: 'Create Failed', detail: err?.error?.message || err?.error || err?.message || 'Failed to create tenant' });
            })
            .finally(() => this.creating.set(false));
    }
}
